import {RegulatoryDocument, Requirement, VexCluster} from 'types';
import {TFunction} from 'react-i18next';
import {DocumentSourceEnum} from 'features/Regulations/RegulationDetail/RegulationDetail.types';
import {IArrayRoles} from 'components/InfinityList/Excel/ExcelExportCommon';

interface ILocationParams {
	regulationId: string | undefined;
	regulatoryDocumentId: string | undefined;
	paragraphId: string | undefined;
	requirementId: string | undefined;
	modelSeries: string | undefined;
	generation: string | undefined;
	baselineId: string | undefined;
	id: string | undefined;
}

interface IExceButtonDisableProps {
	(
		intNumRecords: number,
		arrRoles: IArrayRoles,
		myVexClusters: VexCluster[],
		strLocationType: string,
		requirement: Requirement | undefined,
	): boolean;
}

interface IExceButtonHideProps {
	(entityListItems: any[]): boolean;
}

interface IExcelButtonLocation {
	(pathname: string, params: ILocationParams): string;
}

interface ITooltipXL {
	(strLocationType: string, t: TFunction): string;
}

type ExcelExportRole = keyof IArrayRoles;

const exportRolesByLocation = new Map<string, ExcelExportRole[]>([
	[
		'regulations_page',
		['isAdmin', 'isVko', 'isVex', 'isReader', 'isBedarfskoordinator'],
	],
	[
		'regulation_detail_page',
		['isAdmin', 'isVko', 'isVex', 'isReader', 'isBedarfskoordinator'],
	],
	[
		'regulation_version_detail_page',
		['isAdmin', 'isVko', 'isVex', 'isReader', 'isBedarfskoordinator'],
	],
	['requirements_page', ['isAdmin', 'isVex', 'isBedarfskoordinator']],
	['vehicle_projects_page', ['isAdmin', 'isVex', 'isBedarfskoordinator']],
	['vehicle_project_detail_page', ['isAdmin', 'isVex', 'isBedarfskoordinator']],
	['simulation_detail_page', ['isAdmin', 'isVex', 'isBedarfskoordinator']],
	['baselines_page', ['isAdmin', 'isVex', 'isBedarfskoordinator']],
	['baseline_compare_page', ['isAdmin', 'isVex', 'isBedarfskoordinator']],
	['simulations_page', ['isAdmin', 'isVex', 'isBedarfskoordinator']],
	['vko_vex_page', ['isAdmin', 'isVko', 'isVex', 'isBedarfskoordinator']],
	['pogis_archiv_page', ['isAdmin', 'isVko', 'isVex', 'isBedarfskoordinator']],
	[
		'report_reg_doc_page',
		['isAdmin', 'isVko', 'isVex', 'isBedarfskoordinator'],
	],
]);

const hasExportRole = (roles: IArrayRoles, allowedRoles: ExcelExportRole[]) =>
	allowedRoles.some(role => roles[role]);

const isRequirementExportDisabled = (
	roles: IArrayRoles,
	myVexClusters: VexCluster[],
	requirement: Requirement | undefined,
) => {
	if (roles.isAdmin) return false;
	if (!roles.isVex) return true;

	return !requirement?.vexClusters?.some(requirementCluster =>
		myVexClusters.some(myCluster => myCluster.id === requirementCluster.id),
	);
};

export const excelButtonLocation: IExcelButtonLocation = (pathname, params) => {
	const strLocationTrue = location.pathname
		.split('/')
		.filter(x => x !== '')
		.join('/');

	if (strLocationTrue === 'regulations/' + params.regulationId)
		return 'regulation_detail_page';

	if (
		strLocationTrue ===
			'regulations/' +
				params.regulationId +
				'/' +
				params.regulatoryDocumentId +
				'/paragraphs' ||
		strLocationTrue === 'regulatoryDocuments/' + params.regulatoryDocumentId ||
		strLocationTrue === 'regulatorydocuments/' + params.regulatoryDocumentId
	)
		return 'regulation_version_detail_page';

	if (strLocationTrue === 'requirements/' + params.requirementId)
		return 'requirements_detail_page';

	if (strLocationTrue === 'vehicleProjects') return 'vehicle_projects_page';

	if (
		strLocationTrue ===
		'vehicleProjects/' +
			encodeURIComponent(params.modelSeries ?? '') +
			'/' +
			encodeURIComponent(params.generation ?? '') +
			'/baselines'
	)
		return 'baselines_page';

	if (
		strLocationTrue.startsWith(
			'vehicleProjects/' +
				encodeURIComponent(params.modelSeries ?? '') +
				'/' +
				encodeURIComponent(params.generation ?? '') +
				'/baselines/' +
				params.id +
				'/compare',
		)
	)
		return 'baseline_compare_page';

	if (strLocationTrue === 'simulations') return 'simulations_page';

	if (strLocationTrue === 'simulations/' + params.id)
		return 'simulation_detail_page';

	if (strLocationTrue === 'reports/regulatory-documents')
		return 'report_reg_doc_page';

	if (strLocationTrue === 'VkoVexList') return 'vko_vex_page';

	if (pathname.startsWith('/admin')) {
		return 'admin_page';
	}

	return '';
};

export const excelButtonDisable: IExceButtonDisableProps = (
	intNumRecords,
	arrRoles,
	myVexClusters,
	strLocationType,
	requirement,
) => {
	if (strLocationType === 'requirements_detail_page') {
		return isRequirementExportDisabled(arrRoles, myVexClusters, requirement);
	}

	const allowedRoles = exportRolesByLocation.get(strLocationType);
	if (allowedRoles) return !hasExportRole(arrRoles, allowedRoles);

	if (strLocationType === 'admin_page') {
		return !arrRoles.isAdmin || intNumRecords === 0;
	}

	return true;
};

export const excelButtonHide: IExceButtonHideProps = entityListItems => {
	if (
		entityListItems.length > 0 &&
		entityListItems[0].__typename === 'RegulatoryDocument'
	) {
		const regDoc = entityListItems[0] as RegulatoryDocument;
		return (
			regDoc.documentSource?.name?.toLowerCase() ===
			DocumentSourceEnum.Pogis.toLowerCase()
		);
	}

	return false;
};

export const tooltipXL: ITooltipXL = (strLocationType, t) => {
	const strTrNS = 'components/EntityList/ExcelExport';
	const strTrPrefix = 'TooltipsText';
	const tOptions = {
		ns: strTrNS,
		keyPrefix: strTrPrefix,
	};
	switch (strLocationType) {
		case 'regulations_page':
			return t('Regulations', tOptions);

		case 'regulation_detail_page':
			return t('RegulationDetail', tOptions);

		case 'regulation_version_detail_page':
			return t('RegulationVersionDetail', tOptions);

		case 'requirements_page':
			return t('Requirements', tOptions);

		case 'requirements_detail_page':
			return t('RequirementDetail', tOptions);

		case 'vehicle_projects_page':
			return t('VehicleProjects', tOptions);

		case 'vehicle_project_detail_page':
			return t('VehicleProjectDetail', tOptions);

		case 'baselines_page':
			return t('Baselines', tOptions);

		case 'baseline_detail_page':
			return t('BaselineDetail', tOptions);

		case 'simulations_page':
			return t('Simulations', tOptions);

		case 'simulation_detail_page':
			return t('SimulationDetail', tOptions);

		case 'report_reg_doc_page':
			return t('VoPro', tOptions);

		case 'pogis_archiv_page':
			return t('POGIS', tOptions);

		case 'vko_vex_page':
			return t('VkoVex', tOptions);

		case 'admin_page':
			return t('Admin', tOptions);

		default:
			return 'Excel Export';
	}
};
